import { useState, useEffect } from 'react';
import { Button, Typography, Stack, Divider, Alert, Box, ButtonProps } from '@mui/material';
import { head } from 'ramda';
import { useDispatch } from 'react-redux';

import { useFeatureFlags, useModals, useRouter, useSnackbar } from 'src/hooks';
import { backgroundTasksApi } from 'src/services/BackgroundTask';
import appRoutes from 'src/routes/appRoutes';
import useCertificates from 'src/hooks/talents/useCertificates';
import { ReactComponent as PlusIcon } from 'src/assets/images/plusIcon.svg';
import type Certificate from 'src/types/resources/CertificateType';
import { ModalLayout, ModalType } from 'src/enums/ModalType';
import { isAxiosError, isUnprocessedEntityError } from 'src/utils/responseErrors';
import Loader from 'src/components/Loader';

import Entry from './components/Entry';
import EditableBox from './components/EditableBox';
import styles from './styles';
import useCertificatesText from './hooks/useCertificatesText';

interface TalentCertificatesListProps {
  onClose?: () => void;
  documentsType: 'certificate' | 'resume';
  title?: string;
  isOnboardingWorkflow?: boolean;
  cancelButtonText?: string;
  cancelButtonVariant?: ButtonProps['variant'];
}

const testidPrefixDict = {
  certificate: 'talent-certificates-modal',
  resume: 'talent-resumes-modal',
};

const TalentCertificatesList: React.FC<TalentCertificatesListProps> = ({
  onClose,
  documentsType,
  title,
  isOnboardingWorkflow = false,
  cancelButtonText,
  cancelButtonVariant,
}) => {
  const [errors, setErrors] = useState<string | null>(null);
  const { certificates, isLoadCertificatesFinished, loadCertificates, deleteCertificate } = useCertificates();
  const { showModal, hideModal } = useModals();
  const { pathname } = useRouter();
  const { featureEnableNewTalentOnboarding } = useFeatureFlags();
  const { enqueueSuccessSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const testidPrefix = isOnboardingWorkflow
    ? `${testidPrefixDict[documentsType]}-onboarding`
    : testidPrefixDict[documentsType];

  const isResumesList = documentsType === 'resume';

  const handleDeleteCertificate = (certificate: Certificate) => async () => {
    try {
      await deleteCertificate({ id: certificate.id }).unwrap();
      loadCertificates();
      dispatch(
        backgroundTasksApi.util.invalidateTags([
          {
            type: 'BackgroundTask',
            id: 'generate_work_experience_from_resume_using_ai',
          },
        ]),
      );
    } catch (error: unknown) {
      if (isAxiosError(error) && isUnprocessedEntityError(error)) {
        setErrors(head(error.response.data.errors.nonFieldErrors));
      }
    }
  };

  const handleClose = () => {
    hideModal();
    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    if (certificates.length === 0 && pathname === appRoutes.talent.certificates()) {
      // case for the talent profile certificates page for mobile view
      loadCertificates();
    }
  }, []);

  const heading = useCertificatesText(documentsType);

  const boxVariantStyles = isOnboardingWorkflow ? styles.boxUpdatedStyles : styles.boxOldStyles;
  const boxStyles = { ...styles.box, ...boxVariantStyles };

  if (!isLoadCertificatesFinished) {
    return (
      <Box sx={{ ...boxStyles, ...styles.loaderBox }}>
        <Loader />
      </Box>
    );
  }

  const filteredCertificates = featureEnableNewTalentOnboarding
    ? certificates.filter(cert => (isResumesList ? cert.isResume : !cert.isResume))
    : certificates;

  return (
    <Box sx={boxStyles}>
      {errors && <Alert severity="error">{errors}</Alert>}
      <Box>
        <Typography variant="h3" sx={styles.heading}>
          {title ?? heading}
        </Typography>
        <Divider />
      </Box>
      <Box sx={styles.body}>
        {featureEnableNewTalentOnboarding && isResumesList && (
          <Typography mt={2}>
            Please upload at least one resume to continue. This ensures your profile accurately reflects your experience
            and improves your visibility for new opportunities. Upload resumes only, and add certifications, education
            records, and reference letters in their designated section of your profile.
          </Typography>
        )}
        <Stack spacing={1} sx={styles.certificateList}>
          {filteredCertificates.map(certificate => (
            <EditableBox
              key={certificate.id}
              handleEdit={() => {
                showModal({
                  type: ModalType.TalentCertificateForm,
                  modalLayout: ModalLayout.talentModals,
                  props: {
                    certificate,
                    certificateType: documentsType,
                    onSuccessSubmit: (fileChanged: boolean) => {
                      if (fileChanged) {
                        dispatch(
                          backgroundTasksApi.util.invalidateTags([
                            {
                              type: 'BackgroundTask',
                              id: 'generate_work_experience_from_resume_using_ai',
                            },
                          ]),
                        );
                      }
                    },
                  },
                });
              }}
              handleDelete={handleDeleteCertificate(certificate)}
              hideDelete={filteredCertificates?.length === 1 && isResumesList}
            >
              <Entry key={certificate.id} certificate={certificate} hideEducationDataFields={isResumesList} />
            </EditableBox>
          ))}
        </Stack>

        <Button
          variant="text"
          startIcon={<PlusIcon />}
          onClick={() => {
            showModal({
              type: ModalType.TalentCertificateForm,
              modalLayout: ModalLayout.talentModals,
              props: {
                certificateType: documentsType,
                useUpdatedUI: isOnboardingWorkflow,
                onSuccessSubmit: (fileChanged: boolean) => {
                  if (isOnboardingWorkflow) {
                    enqueueSuccessSnackbar('Resume successfully uploaded.');
                  }
                  if (fileChanged) {
                    dispatch(
                      backgroundTasksApi.util.invalidateTags([
                        {
                          type: 'BackgroundTask',
                          id: 'generate_work_experience_from_resume_using_ai',
                        },
                      ]),
                    );
                  }
                },
              },
            });
          }}
          data-testid={`${testidPrefix}-add-btn`}
        >
          {`Add ${heading}`}
        </Button>
      </Box>
      <Box sx={styles.footer}>
        <Button
          variant={cancelButtonVariant ?? 'outlined'}
          disabled={isOnboardingWorkflow && filteredCertificates.length === 0 && isResumesList}
          sx={isOnboardingWorkflow ? {} : styles.button}
          onClick={handleClose}
          data-testid={`${testidPrefix}-cancel-btn`}
        >
          {cancelButtonText ?? 'Close'}
        </Button>
      </Box>
    </Box>
  );
};

export default TalentCertificatesList;
